import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../utils/firebase';

// Kept in sync with DEAD_TREE_THRESHOLD_DAYS in functions/main.py — display only,
// the actual cutoff is applied server-side when admin/stats is aggregated.
const DEAD_TREE_THRESHOLD_DAYS = 180;

export function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// How many days of history to plot. Ninety keeps the request small and is more
// than any of these lines needs to be legible; the series itself is kept forever.
const TREND_WINDOW_DAYS = 90;

// The lines worth drawing, in reading order. Deliberately short — every chart
// added is one more thing to interpret, and the tables below already answer
// "who" and "which". These answer only "which way".
const TREND_METRICS = [
  { key: 'totalUsers',        label: 'Accounts' },
  { key: 'activeUsers30d',    label: 'Active, 30 days' },
  { key: 'totalPeople',       label: 'People' },
  { key: 'totalTrees',        label: 'Trees' },
  { key: 'paidCount',         label: 'Paying' },
  // Earns its place despite the note above: it is the only line that explains a
  // fall in Paying before it happens, and without it a cancellation is
  // invisible until the paid period ends. Null on every row written before
  // 2026-08-30, which the chart already skips rather than drawing as zero.
  { key: 'cancellingCount',   label: 'Cancelling' },
  { key: 'totalStorageBytes', label: 'Storage', format: formatBytes },
  { key: 'mrrCents',          label: 'MRR', format: (c) => `$${(c / 100).toFixed(2)}` },
];

/**
 * Turns a series into SVG path data.
 *
 * Pure and exported so the geometry can be tested without rendering — the
 * arithmetic here is the only part that can be silently wrong, since a chart
 * with the wrong scale still looks like a chart.
 *
 * Nulls are skipped rather than treated as zero. mrrCents is null until the
 * revenue ledger exists, and the daily deltas are null on any day whose
 * predecessor is missing; plotting those as zero would draw a crash that never
 * happened.
 */
export function buildTrendPath(values, width = 300, height = 80, pad = 6) {
  const points = [];
  values.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) points.push({ i, v });
  });
  if (points.length === 0) return null;

  const n = values.length;
  const vals = points.map((p) => p.v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min;

  // A single reading has no horizontal extent, and a flat series has no
  // vertical one. Both centre rather than divide by zero.
  const xAt = (i) => (n === 1 ? width / 2 : pad + (i / (n - 1)) * (width - pad * 2));
  const yAt = (v) => (span === 0 ? height / 2 : height - pad - ((v - min) / span) * (height - pad * 2));

  const coords = points.map((p) => ({ x: xAt(p.i), y: yAt(p.v) }));
  const line = coords
    .map((c, k) => `${k === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(' ');
  const area = coords.length > 1
    ? `${line} L${coords[coords.length - 1].x.toFixed(2)},${height} L${coords[0].x.toFixed(2)},${height} Z`
    : null;

  return { line, area, last: coords[coords.length - 1], min, max, plotted: coords.length };
}

function TrendCard({ label, values, format }) {
  const geom = buildTrendPath(values);
  const fmt = format || ((v) => v.toLocaleString());
  const plotted = values.filter((v) => typeof v === 'number' && Number.isFinite(v));
  const current = plotted.length ? plotted[plotted.length - 1] : null;

  // Change across the plotted window, not against yesterday: with a daily
  // series the one-day move is mostly noise, and a missed run would read as a
  // fall to zero.
  const change = plotted.length > 1 ? current - plotted[0] : null;

  return (
    <div style={s.trendCard}>
      <div style={s.trendLabel}>{label}</div>
      <div style={s.trendValue}>{current === null ? '—' : fmt(current)}</div>
      {change !== null && change !== 0 && (
        <div style={{ ...s.trendDelta, color: change > 0 ? '#4A8F4E' : '#C0392B' }}>
          {change > 0 ? '▲' : '▼'} {fmt(Math.abs(change))} over {plotted.length} days
        </div>
      )}
      {change === 0 && <div style={s.trendDelta}>flat over {plotted.length} days</div>}

      {geom ? (
        <svg viewBox="0 0 300 80" style={s.trendSvg} role="img" aria-label={`${label} trend`}>
          {geom.area && <path d={geom.area} fill="rgba(196,151,90,0.12)" />}
          <path d={geom.line} fill="none" stroke="#C4975A" strokeWidth="2"
                strokeLinejoin="round" strokeLinecap="round" />
          <circle cx={geom.last.x} cy={geom.last.y} r="3.5" fill="#C4975A" />
        </svg>
      ) : (
        <div style={s.trendEmpty}>not measured yet</div>
      )}

      {geom && geom.plotted === 1 && (
        <div style={s.trendNote}>one reading — a line needs two</div>
      )}
    </div>
  );
}

function Trends({ history, error }) {
  if (error) {
    return <div style={s.trendNote}>Could not load history: {error}</div>;
  }
  if (history === null) {
    return <div style={s.trendNote}>Loading history…</div>;
  }
  if (history.length === 0) {
    return (
      <div style={s.trendNote}>
        No history yet. aggregate_stats appends one row a day at 04:00 UTC — the
        first chart appears after the first run, and a line after the second.
      </div>
    );
  }
  return (
    <div style={s.trendGrid}>
      {TREND_METRICS.map((m) => (
        <TrendCard
          key={m.key}
          label={m.label}
          format={m.format}
          values={history.map((row) => (row[m.key] === undefined ? null : row[m.key]))}
        />
      ))}
    </div>
  );
}

function StatusBadge({ isDead }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      textTransform: 'uppercase', letterSpacing: 0.4,
      background: isDead ? 'rgba(192,57,43,0.1)' : 'rgba(109,174,114,0.15)',
      color: isDead ? '#C0392B' : '#4A8F4E',
    }}>
      {isDead ? 'Dead' : 'Active'}
    </span>
  );
}

function formatAge(hours) {
  if (hours === null) return '';
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export default function AdminDashboard({ onBack }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'admin', 'stats'),
      (snap) => {
        if (snap.exists()) {
          setStats(snap.data());
        } else {
          setError("No stats document yet — the Cloud Function hasn't run. Trigger it manually from the Firebase Console.");
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // One-shot rather than a listener. The series is immutable and gains a single
  // row once a day, so a subscription would stay open all afternoon to learn
  // nothing; admin/stats above keeps its listener because it is overwritten.
  //
  // Read descending and reversed, so the window is the most recent 90 days
  // rather than the first 90 the series ever had.
  useEffect(() => {
    const q = query(
      collection(db, 'admin', 'statsHistory', 'days'),
      orderBy('date', 'desc'),
      limit(TREND_WINDOW_DAYS),
    );
    getDocs(q)
      .then((snap) => setHistory(snap.docs.map((d) => d.data()).reverse()))
      .catch((err) => { setHistory([]); setHistoryError(err.message); });
  }, []);

  // Shown as an age, not just a date. aggregate_stats writes nothing when it
  // fails — a timeout or an OOM leaves the previous document in place — so the
  // only evidence of a broken job is this timestamp not moving, and an absolute
  // date reads as perfectly normal when it is four days old. The job runs daily
  // at 04:00 UTC, so anything past 48h has missed at least one run.
  const updatedDate = stats?.updatedAt?.toDate ? stats.updatedAt.toDate() : null;
  const ageHours = updatedDate ? (Date.now() - updatedDate.getTime()) / 3600000 : null;
  const isStale = ageHours !== null && ageHours > 48;
  const updatedAt = updatedDate ? updatedDate.toLocaleString() : null;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button onClick={onBack} style={s.backBtn}>← Back to Tree</button>
        <h1 style={s.title}>App Dashboard</h1>
        {updatedAt && (
          <span style={isStale ? { ...s.updated, ...s.updatedStale } : s.updated}>
            Last updated {formatAge(ageHours)} · {updatedAt}
            {isStale && ' — the daily job may have failed'}
          </span>
        )}
      </div>

      {loading && (
        <div style={s.center}>
          <div style={s.spinner} />
        </div>
      )}

      {error && (
        <div style={s.errorBox}>{error}</div>
      )}

      {stats && !loading && (
        <div style={s.content}>
          {/* ── Summary cards ── */}
          <div style={s.cardRow}>
            <StatCard label="Total Users"   value={stats.totalUsers}  />
            <StatCard label="Total Trees"   value={stats.totalTrees}  />
            <StatCard label="Total People"  value={stats.totalPeople} />
            <StatCard label="Total Storage" value={formatBytes(stats.totalStorageBytes)} />
            <StatCard
              label={`Reclaimable (${stats.deadTreeCount ?? 0} dead trees)`}
              value={formatBytes(stats.deadTreeStorageBytes)}
              accent="#C0392B"
            />
          </div>

          {/* ── Trends over time ── */}
          <div style={{ ...s.section, marginBottom: 24 }}>
            <h2 style={s.sectionTitle}>
              Trends
              <span style={s.sectionSubtitle}>
                {' '}— last {TREND_WINDOW_DAYS} days, one reading per day
              </span>
            </h2>
            <Trends history={history} error={historyError} />
          </div>

          {/* ── One table: the most active accounts and their tree ──
              Was two tables, Top Users and Trees, which were the same rows
              sorted differently — one tree per account, so a user *is* their
              tree. Ranked by logins now, which is the only column neither of
              the old tables led on and the one that answers "who is actually
              using this". */}
          <div style={s.section}>
            <h2 style={s.sectionTitle}>
              Accounts
              <span style={s.sectionSubtitle}>
                {' '}— top 15 by logins · dead = no visit in {DEAD_TREE_THRESHOLD_DAYS}+ days
              </span>
            </h2>
            {stats.topAccounts?.length > 0 ? (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>#</th>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Email</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Logins</th>
                    <th style={s.th}>Last Login</th>
                    <th style={s.th}>Tree</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>People</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Members</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Storage</th>
                    <th style={s.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topAccounts.map((a, i) => (
                    <tr key={a.uid} style={i % 2 === 0 ? s.rowEven : s.rowOdd}>
                      <td style={s.td}>{i + 1}</td>
                      <td style={s.td}>{a.displayName || '—'}</td>
                      <td style={{ ...s.td, color: '#8B7D6B' }}>{a.email || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{a.loginCount ?? '—'}</td>
                      <td style={{ ...s.td, color: '#8B7D6B', fontSize: 12 }}>
                        {a.lastLoginAt?.toDate ? a.lastLoginAt.toDate().toLocaleDateString() : '—'}
                      </td>
                      {/* An account with no tree is normal: invited members own
                          nothing. Dashes rather than zeroes, so "has no tree" and
                          "has an empty tree" stay different things. */}
                      <td style={s.td}>{a.treeName || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{a.treeId ? a.peopleCount : '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{a.treeId ? a.members : '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right', color: '#8B7D6B' }}>
                        {a.treeId ? formatBytes(a.storageBytes) : '—'}
                      </td>
                      <td style={s.td}>{a.treeId ? <StatusBadge isDead={a.isDead} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: '#8B7D6B', marginTop: 8 }}>No account data yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={s.card}>
      <div style={{ ...s.cardValue, ...(accent ? { color: accent } : {}) }}>{value ?? '—'}</div>
      <div style={s.cardLabel}>{label}</div>
    </div>
  );
}

const s = {
  page: {
    height: '100vh', width: '100vw', background: '#FAF7F2',
    fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
    padding: '32px 40px', overflowY: 'auto',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 20, marginBottom: 32,
    borderBottom: '1px solid #E8E0D4', paddingBottom: 20, flexWrap: 'wrap',
  },
  title: {
    fontSize: 24, fontWeight: 700, color: '#2C2418',
    fontFamily: "'Crimson Pro', Georgia, serif", margin: 0, flex: 1,
  },
  updated: {
    fontSize: 12, color: '#A89880',
  },
  updatedStale: {
    color: '#C0392B', fontWeight: 600,
  },
  backBtn: {
    background: 'none', border: '1px solid #D4C9BB', borderRadius: 6,
    padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#5C4B3A',
    fontFamily: "'DM Sans', sans-serif",
  },
  center: {
    display: 'flex', justifyContent: 'center', paddingTop: 80,
  },
  spinner: {
    width: 32, height: 32,
    border: '3px solid #E8E0D4',
    borderTop: '3px solid #C4975A',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorBox: {
    background: '#FFF3CD', border: '1px solid #F0C040', borderRadius: 8,
    padding: '14px 18px', color: '#5C4B3A', fontSize: 14, maxWidth: 600,
  },
  content: {
    maxWidth: 900,
  },
  cardRow: {
    display: 'flex', gap: 16, marginBottom: 36, flexWrap: 'wrap',
  },
  card: {
    flex: '1 1 180px', background: '#fff', border: '1px solid #E8E0D4',
    borderRadius: 10, padding: '24px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  cardValue: {
    fontSize: 40, fontWeight: 700, color: '#C4975A',
    fontFamily: "'Crimson Pro', Georgia, serif", lineHeight: 1,
  },
  cardLabel: {
    fontSize: 13, color: '#8B7D6B', marginTop: 6, fontWeight: 500,
  },
  section: {
    background: '#fff', border: '1px solid #E8E0D4', borderRadius: 10,
    padding: '24px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  sectionTitle: {
    fontSize: 16, fontWeight: 600, color: '#2C2418', margin: '0 0 16px',
  },
  sectionSubtitle: {
    fontSize: 12, fontWeight: 400, color: '#A89880', textTransform: 'none', letterSpacing: 0,
  },
  trendGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 20,
  },
  trendCard: {
    minWidth: 0,
  },
  trendLabel: {
    fontSize: 12, color: '#8B7D6B', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  trendValue: {
    fontSize: 26, fontWeight: 700, color: '#2C2418',
    fontFamily: "'Crimson Pro', Georgia, serif", lineHeight: 1.2, marginTop: 2,
  },
  trendDelta: {
    fontSize: 11, color: '#A89880', marginBottom: 6,
  },
  trendSvg: {
    width: '100%', height: 'auto', display: 'block', overflow: 'visible',
  },
  trendEmpty: {
    fontSize: 12, color: '#A89880', fontStyle: 'italic', padding: '20px 0',
  },
  trendNote: {
    fontSize: 12, color: '#A89880', marginTop: 6,
  },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 14,
  },
  th: {
    textAlign: 'left', padding: '8px 12px', color: '#8B7D6B',
    fontWeight: 600, fontSize: 12, textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid #E8E0D4',
  },
  td: {
    padding: '10px 12px', color: '#2C2418', verticalAlign: 'middle',
  },
  rowEven: { background: '#FDFAF6' },
  rowOdd:  { background: '#fff' },
};
